/**
 * Center overlay inside wheel: 3-line dynamic status text + circular progress timer.
 */

import { useId, useMemo } from "react"
import { useShallow } from "zustand/react/shallow"
import { usePvpRoomsStore } from "@/entities/pvp-roulette"
import {
  useLivePvpRoomPhase,
  useLivePvpHubUi,
  usePvpRoomPhaseDurations,
  usePvpRoomBanks,
  usePvpRoomParties,
  usePvpRoomMyBetPartyId,
  usePvpRoomActiveBets,
} from "../hooks/usePvpRoomSelectors"
import { usePvpRoomOptimisticPending } from "@/entities/pvp-roulette"
import { getUserIdFromToken } from "@/shared/api/auth"
import { WHEEL_INNER_RADIUS_RATIO } from "@/shared/constants/pvpRoulette"
import {
  formatHundredthsTrimZeros,
  formatTonCompact,
  normalizePercent,
  toSafeNumber,
} from "@/shared/lib/pvpRouletteMath"
import {
  getEffectiveTiming,
  normalizeUnixTimestampToMs,
} from "@/shared/api/types/pvpRoulette"
import { useI18n } from "@/shared/i18n"

import TonIcon from "@/assets/icons/ton-icon.svg?react"

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.min(1, Math.max(0, n))
}

interface WheelCenterOverlayProps {
  roomId: string | null
  /** Optional external clock (ms). Used by dev emulator fixture timeline. */
  nowMs?: number | null
}

/** Hub diameter = 2 * innerRadius (matches WHEEL_INNER_RADIUS_RATIO and PNG plate center). */
const HUB_DIAMETER_FRAC = 2 * WHEEL_INNER_RADIUS_RATIO

/**
 * Figma Text/12-Regular (Place your bets, You: n%, …): Inter 400, 12px, line-height 16px,
 * letter-spacing -0.5px, white 60%. Row box 16px (`h-4`); design export often shows ~81×16 as
 * intrinsic width — we do not set `width: 81px` so longer i18n strings stay on one line.
 */
const HUB_TEXT_12_REGULAR =
  "flex h-4 w-full max-w-full shrink-0 items-center justify-center px-0 text-center font-inter text-[12px] font-normal leading-[16px] tracking-[-0.5px] text-white/60"

// SVG in viewBox units — scales with hub box via width/height 100%
// R_VB = (VB − stroke)/2 keeps outer edge on hub rim; thicker stroke grows inward only.
const VB = 100
const STROKE_VB = 6 /* viewBox units; scales with hub */
const R_VB = (VB - STROKE_VB) / 2
const C_VB = 2 * Math.PI * R_VB
const CX_VB = VB / 2
const CY_VB = VB / 2
const TWO_PI = 2 * Math.PI

/** Light at arc head (12 o’clock → clockwise), dark at tail — stroke paint only (`linearGradient`). */
const HUB_RING_BETTING = { light: "#B2FE00", dark: "#00FE74" } as const
const HUB_RING_TRICKS = { light: "#FE7200", dark: "#FE2A00" } as const

function hubRingGradientChordEnds(
  cx: number,
  cy: number,
  r: number,
  sweepRadRaw: number
): { gx1: number; gy1: number; gx2: number; gy2: number } {
  const θ0 = -Math.PI / 2
  let sweep = sweepRadRaw
  if (sweep <= 1e-5) sweep = 1e-5
  if (sweep >= TWO_PI - 1e-5) sweep = TWO_PI - 1e-5
  const θ1 = θ0 + sweep
  return {
    gx1: cx + r * Math.cos(θ0),
    gy1: cy + r * Math.sin(θ0),
    gx2: cx + r * Math.cos(θ1),
    gy2: cy + r * Math.sin(θ1),
  }
}

export const WheelCenterOverlay = ({
  roomId,
  nowMs = null,
}: WheelCenterOverlayProps) => {
  const { t } = useI18n()
  const hubRingGradBettingId = useId()
  const hubRingGradTricksId = useId()

  const phase = useLivePvpRoomPhase(roomId, { nowMs })
  const hubUi = useLivePvpHubUi(roomId, { nowMs })
  const durations = usePvpRoomPhaseDurations(roomId)
  const ringTiming = usePvpRoomsStore(
    useShallow((s) => {
      const r = roomId ? s.rooms[roomId]?.rawRound : null
      if (!r) return null
      const t = getEffectiveTiming(r)
      const bs = t.bettingStartedAt ?? r.bettingStartedAt
      const be = t.bettingEndsAt ?? r.bettingEndsAt
      const bettingStartedAt =
        bs != null ? normalizeUnixTimestampToMs(bs) : null
      const bettingEndsAt = be != null ? normalizeUnixTimestampToMs(be) : null
      const tricksStartRaw = r.tricksStartedAt ?? r.spinningEndsAt
      const tricksStartedAt =
        tricksStartRaw != null
          ? normalizeUnixTimestampToMs(tricksStartRaw)
          : null
      const tricksEndsAtRaw =
        r.tricksEndsAt != null
          ? normalizeUnixTimestampToMs(r.tricksEndsAt)
          : null
      return {
        roundId: r.roundId,
        bettingStartedAt,
        bettingEndsAt,
        tricksStartedAt,
        tricksEndsAtRaw,
      }
    })
  )

  const banks = usePvpRoomBanks(roomId)
  const parties = usePvpRoomParties(roomId)
  const activeBets = usePvpRoomActiveBets(roomId)
  const optimisticPending = usePvpRoomOptimisticPending(roomId)

  const userId = getUserIdFromToken()

  const myPartyId = usePvpRoomMyBetPartyId(roomId, userId)

  const liveBettingRemaining = hubUi.remainingMsBetting
  const liveTricksRemaining = hubUi.remainingMsTricks

  const hasActiveBets = activeBets.length > 0
  const hasOptimisticPendingBet = optimisticPending != null
  const hasAnyBet = hasActiveBets || hasOptimisticPendingBet
  const activeBetsSum = activeBets.reduce(
    (sum, b) => sum + toSafeNumber(b.amount, 0),
    0
  )
  const optimisticAmount = optimisticPending?.amount ?? 0
  const snapshotPrizePool = toSafeNumber(banks?.prizePoolTon, 0)
  const optimisticAdjustedPool = activeBetsSum + optimisticAmount
  const prizePool = hasAnyBet
    ? hasActiveBets
      ? optimisticAdjustedPool
      : Math.max(snapshotPrizePool, optimisticAmount)
    : 0
  const displayAmount = formatTonCompact(prizePool)

  const winChance = useMemo(() => {
    if (!myPartyId || parties.length === 0) return null
    const myParty = parties.find(
      (p) =>
        p.partyId === myPartyId ||
        p.userId === myPartyId ||
        `solo:${p.userId}` === myPartyId
    )
    if (!myParty) return null
    return normalizePercent(myParty.percent)
  }, [myPartyId, parties])

  // Progress ring (physics unchanged: dash tween + −90°; color via chord linearGradient ↓).
  let progress = 0
  const isBetting = phase === "BETTING"
  const isTricks = phase === "TRICKS"

  if (isBetting && liveBettingRemaining != null) {
    const s = ringTiming?.bettingStartedAt
    const e = ringTiming?.bettingEndsAt
    const windowMs =
      s != null && e != null && e > s ? e - s : durations?.bettingDurationMs
    if (windowMs != null && windowMs > 0) {
      progress = clamp01(1 - liveBettingRemaining / windowMs)
    }
  } else if (isTricks && liveTricksRemaining != null) {
    const tricksDurationMs = durations?.tricksDurationMs
    if (tricksDurationMs != null && tricksDurationMs > 0) {
      progress = clamp01(1 - liveTricksRemaining / tricksDurationMs)
    }
  }

  const showRing =
    (isBetting && liveBettingRemaining != null) ||
    (isTricks && liveTricksRemaining != null)
  const isWaiting = phase === "WAITING"
  const isWaitingWithoutBets = isWaiting && !hasAnyBet
  const isResolved = phase === "RESOLVED"

  const titleText = isWaitingWithoutBets ? t("pvpRoom.placeYourBets") : null
  const subtitleText = isWaitingWithoutBets
    ? null
    : isWaiting
        ? t("pvpRoom.waitingForPlayers")
        : t("pvpRoom.winChanceYou", {
            percent: formatHundredthsTrimZeros(winChance ?? 0),
          })

  const shouldRenderOverlay = true

  if (!phase || !shouldRenderOverlay) return null

  /**
   * Circle + dashed stroke: dashoffset interpolates floats each rAF — smooth.
   * `rotate(-90)` fixes the stroke endpoint at **12 o’clock**; negative offset so the
   * visible arc shrinks **clockwise** (into the right side first), not CCW toward 9.
   */
  const ringDashOffset = -C_VB * progress
  const ringChord = hubRingGradientChordEnds(CX_VB, CY_VB, R_VB, (1 - progress) * TWO_PI)
  const ringStrokePaint = isBetting
    ? `url(#${hubRingGradBettingId})`
    : `url(#${hubRingGradTricksId})`

  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
      <div
        className="relative aspect-square max-h-full max-w-full"
        style={{ width: `${HUB_DIAMETER_FRAC * 100}%` }}
      >
        {/* Dark background circle */}
        <div className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden rounded-full bg-black-100">
          {/* Hub copy: row1 — 20 Black + ~13 TON; secondary — HUB_TEXT_12_REGULAR. Column width from hub (max-w) so labels don’t wrap. */}
          <div className="flex min-w-0 w-full max-w-[min(124px,calc(100%-4px))] flex-col items-center px-0">
            <span className="flex min-h-[24px] items-center justify-center gap-1 font-inter text-[20px] font-black leading-6 tracking-normal tabular-nums text-white">
              {isWaitingWithoutBets ? (
                t("pvpRoom.waiting")
              ) : (
                <>
                  {displayAmount} <TonIcon className="size-[13px] shrink-0" />
                </>
              )}
            </span>
            {titleText != null && (
              <span className={HUB_TEXT_12_REGULAR}>{titleText}</span>
            )}
            {subtitleText && (
              <span className={HUB_TEXT_12_REGULAR}>{subtitleText}</span>
            )}
          </div>
        </div>

        {/* SVG progress ring — radius tracks Pixi inner hub via shared ratio */}
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox={`0 0 ${VB} ${VB}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient
              id={hubRingGradBettingId}
              gradientUnits="userSpaceOnUse"
              x1={ringChord.gx1}
              y1={ringChord.gy1}
              x2={ringChord.gx2}
              y2={ringChord.gy2}
            >
              <stop offset="0%" stopColor={HUB_RING_BETTING.light} />
              <stop offset="100%" stopColor={HUB_RING_BETTING.dark} />
            </linearGradient>
            <linearGradient
              id={hubRingGradTricksId}
              gradientUnits="userSpaceOnUse"
              x1={ringChord.gx1}
              y1={ringChord.gy1}
              x2={ringChord.gx2}
              y2={ringChord.gy2}
            >
              <stop offset="0%" stopColor={HUB_RING_TRICKS.light} />
              <stop offset="100%" stopColor={HUB_RING_TRICKS.dark} />
            </linearGradient>
          </defs>
          {showRing && (
            <g transform={`rotate(-90 ${CX_VB} ${CY_VB})`}>
              <circle
                cx={CX_VB}
                cy={CY_VB}
                r={R_VB}
                fill="none"
                stroke={ringStrokePaint}
                strokeWidth={STROKE_VB}
                strokeLinecap="butt"
                strokeDasharray={`${C_VB} ${C_VB}`}
                strokeDashoffset={ringDashOffset}
              />
            </g>
          )}
        </svg>
      </div>
    </div>
  )
}
